import React, { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  Camera,
  ChevronDown,
  Globe,
  LogIn,
  LogOut,
  Menu,
  Search,
  Stamp,
  X,
} from "lucide-react";
import { useAuth } from "../../AuthContext";
import NotificationBell from "../NotificationBell";
import BottoneInstallaApp from "../BottoneInstallaApp";
import CustodeProfileWidget from "./CustodeProfileWidget";
import { useLang } from "../../LangContext";
import { placesSearch } from "../../api";
import { langUrl, URL_LANGS } from "../../utils/langRoute";
import { loginUrlForPage } from "../../utils/townReturn";

const BORGHI_SEARCH = [
  { name: "Civitella del Tronto", slug: "civitella-del-tronto" },
  { name: "Castelli", slug: "castelli" },
  { name: "Assisi", slug: "assisi" },
  { name: "Montorio al Vomano", slug: "montorio-al-vomano" },
  { name: "Offida", slug: "offida" },
  { name: "Campli", slug: "campli" },
];

const NAV_LINKS = [
  { to: "/piazze", tKey: "nav.piazze", label: "Piazza" },
  { to: "/esplora-la-mappa", tKey: "nav.map3d", label: "Mappa 3D" },
  { to: "/eventi", tKey: "nav.events", label: "Eventi", overflowXl: true },
  { to: "/vetrina", tKey: "nav.merchants", label: "Vetrina", overflowXl: true },
];

const LANGS = ["it", "en", "fr", "de", "es", "pt"];

function readTimbriCount() {
  try {
    const raw = localStorage.getItem("6amico_passaporto_timbri");
    if (!raw) return 0;
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed.length;
    if (typeof parsed === "number") return parsed;
    return 0;
  } catch {
    return 0;
  }
}

const LINGUE_META = {
  it: { label: "Italiano", emoji: "🇮🇹" },
  en: { label: "English", emoji: "🇬🇧" },
  fr: { label: "Français", emoji: "🇫🇷" },
  de: { label: "Deutsch", emoji: "🇩🇪" },
  es: { label: "Español", emoji: "🇪🇸" },
  pt: { label: "Português", emoji: "🇵🇹" },
};

/** Selettore lingua a scomparsa (IT/EN/FR/DE/ES) — condiviso navbar e footer.
 *  BLOCCO C: it/en/fr/es sono link <a href> veri alle URL per lingua
 *  (indicizzabili); le altre lingue restano bottoni → Google Translate,
 *  senza URL dedicata (non indicizzate). */
export function LanguageSwitch({ compact = false }) {
  const { lang, setLang } = useLang();
  const current = LANGS.includes(lang) ? lang : "it";
  const [aperto, setAperto] = useState(false);
  const containerRef = useRef(null);
  const { pathname, search } = useLocation();

  useEffect(() => {
    const handleClickFuori = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setAperto(false);
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") setAperto(false);
    };
    document.addEventListener("mousedown", handleClickFuori);
    document.addEventListener("touchstart", handleClickFuori);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", handleClickFuori);
      document.removeEventListener("touchstart", handleClickFuori);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  const pick = (code) => {
    try {
      localStorage.setItem("6amico_lang", code);
    } catch { /* noop */ }
    window.dispatchEvent(
      new CustomEvent("6amico_language_changed", { detail: { lang: code } })
    );
    setLang(code);
    setAperto(false);
  };

  return (
    <div ref={containerRef} className="relative inline-block text-left">
      <button
        type="button"
        onClick={() => setAperto((v) => !v)}
        aria-expanded={aperto}
        aria-haspopup="menu"
        aria-label={`Lingua attuale: ${LINGUE_META[current].label}. Clicca per cambiare`}
        data-testid="lang-switch"
        className={`flex items-center gap-1.5 px-3.5 min-h-[48px] min-w-[48px] rounded-full border border-[#ecd9c2] bg-[#FDFBF7] hover:border-[#C26D4A] text-[#162D4A] text-sm font-semibold tracking-wide shadow-sm transition-all focus:outline-none focus:ring-2 focus:ring-[#162d4a]`}
      >
        <Globe size={15} className="text-[#C26D4A]" aria-hidden />
        <span>{current.toUpperCase()}</span>
        <ChevronDown
          size={12}
          className={`text-stone-400 transition-transform duration-200 ${aperto ? "rotate-180" : ""}`}
          aria-hidden
        />
      </button>

      {aperto && (
        <div
          className="absolute right-0 mt-1.5 w-44 rounded-xl bg-white border border-stone-200 shadow-xl py-1.5 z-50"
          role="menu"
          aria-label="Seleziona lingua"
        >
          <div className="px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-stone-400 border-b border-stone-100 mb-1">
            Seleziona Lingua · Choose language
          </div>
          {LANGS.map((code) => {
            const meta = LINGUE_META[code];
            const isSelected = code === current;
            const optionClass = `w-full min-h-[48px] flex items-center justify-between px-3 py-2.5 text-sm text-left transition-colors ${
              isSelected
                ? "bg-[#162d4a] text-white font-semibold"
                : "text-stone-700 hover:bg-stone-100 font-medium"
            }`;
            const inner = (
              <>
                <span className="flex items-center gap-2">
                  <span className="text-sm leading-none" aria-hidden>{meta.emoji}</span>
                  <span>{meta.label}</span>
                </span>
                <span className={`text-[10px] tracking-wider uppercase ${isSelected ? "text-stone-200" : "text-stone-400"}`}>
                  {code}
                </span>
              </>
            );
            // it/en/fr/es: link vero alla URL per lingua (SEO).
            // Altre lingue (de, …): bottone → Google Translate, non indicizzate.
            if (code === "it" || URL_LANGS.includes(code)) {
              return (
                <a
                  key={code}
                  href={langUrl(code, pathname, search)}
                  role="menuitem"
                  aria-pressed={isSelected}
                  data-testid={`lang-option-${code}`}
                  className={optionClass}
                  onClick={() => {
                    try {
                      localStorage.setItem("6amico_lang", code);
                      localStorage.setItem("6amico_lang_explicit", "1");
                    } catch { /* noop */ }
                  }}
                >
                  {inner}
                </a>
              );
            }
            return (
              <button
                key={code}
                type="button"
                role="menuitem"
                onClick={() => pick(code)}
                aria-pressed={isSelected}
                data-testid={`lang-option-${code}`}
                className={optionClass}
              >
                {inner}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function NavbarTerritorialeTop() {
  const { user, logout } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const loginHref = loginUrlForPage(pathname, search);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [drawer, setDrawer] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [timbri, setTimbri] = useState(0);
  const searchRef = useRef(null);
  const moreRef = useRef(null);

  useEffect(() => {
    setTimbri(readTimbriCount());
    const bump = () => setTimbri(readTimbriCount());
    window.addEventListener("6amico_timbro_aggiunto", bump);
    window.addEventListener("storage", bump);
    return () => {
      window.removeEventListener("6amico_timbro_aggiunto", bump);
      window.removeEventListener("storage", bump);
    };
  }, []);

  useEffect(() => {
    setDrawer(false);
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    const onDoc = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) setOpen(false);
      if (moreRef.current && !moreRef.current.contains(e.target)) setMoreOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const [apiResults, setApiResults] = useState([]);
  const [mobileSearch, setMobileSearch] = useState(false);

  // Ricerca unificata: /places/search copre comuni, villages, frazioni e
  // world_cities — debounce 220ms, fallback alla lista curata a query vuota.
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) { setApiResults([]); return undefined; }
    let alive = true;
    const timer = setTimeout(async () => {
      try {
        const data = await placesSearch(q, 12);
        if (alive) setApiResults(Array.isArray(data) ? data : []);
      } catch {
        if (alive) setApiResults([]);
      }
    }, 220);
    return () => { alive = false; clearTimeout(timer); };
  }, [query]);

  const results = query.trim().length >= 2
    ? apiResults
    : BORGHI_SEARCH.map((b) => ({ ...b, kind: "comune", region: "Italia" }));
  const italiResults = results.filter((r) => r.kind !== "world");
  const worldResults = results.filter((r) => r.kind === "world");

  const goBorgo = (r) => {
    setOpen(false);
    setMobileSearch(false);
    setQuery("");
    const kind = r?.kind || "comune";
    if (kind === "world") navigate(`/citta/${r.slug}`);
    else if (kind === "frazione" && r.village_slug) navigate(`/comune/${r.village_slug}`);
    else navigate(`/comune/${r.slug}`);
  };

  const searchAll = () => {
    const q = query.trim();
    setOpen(false);
    setMobileSearch(false);
    setQuery("");
    navigate(`/comuni?q=${encodeURIComponent(q)}`);
  };

  const SearchItems = () => (
    <>
      {results.map((b) => (
        <li key={`${b.kind}-${b.slug}`} role="option" aria-selected="false">
          <button
            type="button"
            onClick={() => goBorgo(b)}
            className="w-full text-left px-4 min-h-[44px] flex items-center justify-between gap-2 text-sm text-stone-800 hover:bg-[#f7efe6] hover:text-stone-950"
          >
            <span className="truncate">{b.name}</span>
            <span className="text-[10px] text-stone-500 shrink-0">{b.region || b.country || ""}</span>
          </button>
        </li>
      ))}
      {query.trim().length >= 2 && (
        <li role="option" aria-selected="false">
          <button
            type="button"
            onClick={searchAll}
            className="w-full text-left px-4 min-h-[44px] flex items-center gap-2 text-sm font-semibold text-[#a85837] hover:bg-[#f7efe6] border-t border-[#eedfcb]"
          >
            🔎 {results.length === 0
              ? `Nessun risultato — cerca «${query.trim()}» ovunque`
              : `Cerca «${query.trim()}» ovunque`}
          </button>
        </li>
      )}
    </>
  );

  return (
    <header
      className="sticky top-0 z-40 bg-[#fdfbf7]/95 backdrop-blur border-b border-[#eedfcb]"
      data-testid="navbar-territoriale"
    >
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 flex items-center gap-3 h-16">
        <Link
          to="/"
          className="flex items-center gap-2.5 shrink-0 min-h-[48px]"
          aria-label="6AMICO — torna alla home"
        >
          <span
            className="w-10 h-10 rounded-full bg-[#c26d4a] text-white font-serif font-bold text-lg flex items-center justify-center border-2 border-[#a85837] shadow-sm"
            aria-hidden
          >
            6a
          </span>
          <span className="hidden sm:flex flex-col leading-none">
            <span className="font-serif font-bold text-xl text-stone-950 tracking-wide">
              6AMICO
            </span>
            <span className="hidden xl:block whitespace-nowrap text-[10px] font-sans font-medium text-stone-600 tracking-wide mt-0.5">
              {t("nav.tagline") || "La rete dei borghi d'Italia · Dal 2013"}
            </span>
          </span>
        </Link>

        {/* Mobile: icona cerca → overlay full-width */}
        <button
          type="button"
          onClick={() => { setMobileSearch(v => !v); setOpen(true); }}
          className="md:hidden inline-flex min-w-[44px] min-h-[44px] items-center justify-center rounded-full text-stone-800 hover:bg-[#f7efe6] transition-colors shrink-0"
          aria-label={mobileSearch ? "Chiudi ricerca" : "Cerca un borgo"}
          aria-expanded={mobileSearch}
          data-testid="nav-mobile-search-toggle"
        >
          {mobileSearch ? <X size={20}/> : <Search size={20}/>}
        </button>

        <nav className="hidden lg:flex items-center gap-6" aria-label="Navigazione principale">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              className={({ isActive }) => `${l.overflowXl ? "hidden xl:inline-flex" : "inline-flex"} min-h-[44px] items-center whitespace-nowrap text-[15px] font-sans font-medium text-[#162D4A] hover:text-[#a85837] transition-colors ${isActive ? "underline underline-offset-8 decoration-[#b8860b] decoration-2" : ""}`}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-4 ml-auto shrink-0">
          {/* Altro — menu overflow nav (Eventi/Vetrina sotto i 1280px) */}
          <div ref={moreRef} className="relative">
            <button
              type="button"
              onClick={() => setMoreOpen((v) => !v)}
              aria-expanded={moreOpen}
              aria-haspopup="menu"
              className="min-h-[44px] inline-flex items-center gap-1.5 px-3.5 rounded-full border border-[#ecd9c2] bg-[#f7efe6] text-sm font-sans font-semibold text-stone-900 hover:border-[#162d4a] transition-colors"
              data-testid="nav-more-toggle"
            >
              {t("nav.more") || "Altro"}
              <ChevronDown size={13} className={`transition-transform ${moreOpen ? "rotate-180" : ""}`} aria-hidden />
            </button>
            {moreOpen && (
              <div
                role="menu"
                aria-label="Altre funzioni"
                className="absolute right-0 mt-1.5 w-56 rounded-xl bg-white border border-stone-200 shadow-xl py-1.5 z-50"
              >
                {NAV_LINKS.filter((l) => l.overflowXl).map((l) => (
                  <Link
                    key={l.to}
                    to={l.to}
                    role="menuitem"
                    onClick={() => setMoreOpen(false)}
                    className="xl:hidden w-full flex items-center gap-2.5 px-4 min-h-[44px] text-sm text-stone-800 hover:bg-[#f7efe6]"
                  >
                    {l.label}
                  </Link>
                ))}
                <Link
                  to="/qr"
                  role="menuitem"
                  onClick={() => setMoreOpen(false)}
                  className="w-full flex items-center gap-2.5 px-4 min-h-[44px] text-sm text-stone-800 hover:bg-[#f7efe6]"
                >
                  <Camera size={15} className="text-stone-500" aria-hidden />
                  {t("nav.ar_qr") || "Visore AR & QR"}
                </Link>
                <div className="px-3 py-1.5" role="none">
                  <BottoneInstallaApp variante="card" />
                </div>
              </div>
            )}
          </div>

          {/* Ricerca — icona: si espande a overlay centrato al click */}
          <div
            ref={searchRef}
            className={`nav-search ${open
              ? "absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 w-[min(640px,88vw)] z-50"
              : "relative shrink-0"}`}
          >
            <label htmlFor="nav-search-borgo" className="sr-only">
              {t("nav.search_aria") || "Cerca un borgo"}
            </label>
            {open ? (
              <div className="flex items-center gap-2 h-11 px-3 rounded-full bg-white border border-[#c26d4a] shadow-xl">
                <Search size={16} className="text-stone-700 shrink-0" aria-hidden />
                <input
                  id="nav-search-borgo"
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && results[0]) goBorgo(results[0]);
                    if (e.key === "Escape") setOpen(false);
                  }}
                  placeholder={t("nav.search_placeholder") || "Cerca un borgo o una città…"}
                  autoComplete="off"
                  autoFocus
                  className="w-full bg-transparent text-sm text-stone-950 placeholder:text-stone-600 outline-none"
                  aria-expanded={open}
                  aria-controls="nav-search-list"
                  role="combobox"
                />
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setOpen(true)}
                className="min-h-[44px] min-w-[44px] inline-flex items-center justify-center rounded-full bg-white border border-[#eedfcb] text-stone-700 hover:border-[#c26d4a] transition-colors"
                aria-label={t("nav.search_aria") || "Cerca un borgo"}
                data-testid="nav-search-trigger"
              >
                <Search size={17} aria-hidden />
              </button>
            )}
            {open && (results.length > 0 || query.trim().length >= 2) && (
              <ul
                id="nav-search-list"
                role="listbox"
                className="absolute top-full left-0 right-0 mt-2 bg-white border border-[#eedfcb] rounded-xl shadow-lg overflow-hidden z-50 max-h-[70dvh] overflow-y-auto"
              >
                <SearchItems/>
              </ul>
            )}
          </div>

          {/* Francobolli del Passaporto */}
          {(user || timbri > 0) && (
            <Link
              to="/passaporto"
              className="inline-flex min-h-[44px] items-center gap-1.5 px-3.5 rounded-full bg-[#b8860b] text-white text-sm font-sans font-bold shadow-sm"
              title={`Francobolli collezionati: ${timbri} su 6`}
              aria-label={`Francobolli collezionati: ${timbri} su 6`}
              data-testid="passaporto-badge"
            >
              <Stamp size={15} aria-hidden />
              {timbri}/6
              <span className="hidden min-[1440px]:inline text-[10px] font-mono uppercase tracking-wider">Francobolli</span>
            </Link>
          )}
          <LanguageSwitch compact />
          {user ? (
            <>
              <NotificationBell />
              <CustodeProfileWidget user={user} onLogout={logout} />
            </>
          ) : (
            <Link
              to={loginHref}
              className="min-h-[44px] inline-flex items-center gap-1.5 px-4 rounded-full bg-stone-900 text-white text-sm font-sans font-semibold hover:bg-stone-700 transition-colors"
              data-testid="nav-login-cta"
            >
              <LogIn size={15} aria-hidden />
              {t("nav.login") || "Accedi"}
            </Link>
          )}
        </div>

        <button
          type="button"
          onClick={() => setDrawer((v) => !v)}
          className="md:hidden ml-auto min-h-[48px] min-w-[48px] inline-flex items-center justify-center rounded-full text-stone-900 hover:bg-[#f7efe6]"
          aria-expanded={drawer}
          aria-label={drawer ? "Chiudi menu" : "Apri menu"}
          data-testid="nav-mobile-toggle"
        >
          {drawer ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {mobileSearch && (
        <div className="md:hidden border-t border-[#eedfcb] bg-[#fdfbf7] px-4 py-3">
          <div className="flex items-center gap-2 h-11 px-3 rounded-full bg-white border border-[#eedfcb] focus-within:border-[#c26d4a]">
            <Search size={16} className="text-stone-700 shrink-0" aria-hidden />
            <input
              type="search"
              value={query}
              onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && results[0]) goBorgo(results[0]);
                if (e.key === "Escape") setMobileSearch(false);
              }}
              placeholder={t("nav.search_placeholder") || "Cerca un borgo o una città…"}
              autoComplete="off"
              autoFocus
              className="w-full bg-transparent text-sm text-stone-950 placeholder:text-stone-600 outline-none"
              aria-label={t("nav.search_aria") || "Cerca un borgo"}
            />
          </div>
          {open && (results.length > 0 || query.trim().length >= 2) && (
            <ul role="listbox" className="mt-2 bg-white border border-[#eedfcb] rounded-xl overflow-hidden max-h-[60dvh] overflow-y-auto">
              <SearchItems/>
            </ul>
          )}
        </div>
      )}

      {drawer && (
        <nav
          className="md:hidden border-t border-[#eedfcb] bg-[#fdfbf7] px-4 py-3 flex flex-col gap-1"
          aria-label="Menu mobile"
          data-testid="nav-mobile-drawer"
        >
          {NAV_LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className="min-h-[48px] flex items-center px-3 rounded-lg text-base font-sans font-semibold text-stone-900 hover:bg-[#f7efe6]"
            >
              {t(l.tKey)}
            </Link>
          ))}
          <Link
            to="/qr"
            className="min-h-[48px] flex items-center gap-2 px-3 rounded-lg text-base font-sans font-semibold text-stone-900 hover:bg-[#f7efe6]"
          >
            <Camera size={17} aria-hidden />
            {t("nav.ar_qr") || "Visore AR & QR"}
          </Link>
          <div className="flex items-center gap-2 px-3 py-2">
            <span className="inline-flex items-center gap-1.5 px-3.5 min-h-[44px] rounded-full bg-[#b8860b] text-white text-sm font-sans font-bold shadow-sm">
              <Stamp size={15} aria-hidden />
              {timbri}/6
            </span>
            <LanguageSwitch />
          </div>
          <div className="px-3 pb-1">
            <BottoneInstallaApp variante="card" />
          </div>
          {user ? (
            <>
              <CustodeProfileWidget user={user} compact />
              <button
                type="button"
                onClick={logout}
                className="min-h-[48px] flex items-center gap-2 px-3 rounded-lg text-base font-sans font-semibold text-stone-900 hover:bg-[#f7efe6]"
              >
                <LogOut size={17} aria-hidden />
                {t("nav.logout") || "Esci"}
              </button>
            </>
          ) : (
            <Link
              to={loginHref}
              className="min-h-[48px] flex items-center justify-center gap-2 px-3 rounded-lg bg-stone-900 text-white text-base font-sans font-semibold"
              data-testid="nav-login-cta-mobile"
            >
              <LogIn size={17} aria-hidden />
              {t("nav.login_or_register") || "Accedi o registrati"}
            </Link>
          )}
        </nav>
      )}
    </header>
  );
}
